// CMS product-test (dry-run) schemas.
//
// Port of the legacy `GET /cms-test/products` surface
// (travel_planner_back/src/cms-test): simulates the public CMS product
// delivery query with verbose hashtag scoring so editors can see exactly
// which products the storefront would return for a hashtag/filter set —
// including association expansion, per-product score breakdowns, and
// backfill/random fill source labels.

import { z } from 'zod';
import { cmsProductListItemSchema } from './cms';

export const testCmsProductsInput = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  title: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  country: z.array(z.string().min(1)).optional(),
  // Stringly-typed like the other CMS product inputs — the adapter coerces
  // to the category enum at the storage boundary.
  contentCategory: z.array(z.string().min(1)).optional(),
  contentSubcategory: z.array(z.string().min(1)).optional(),
  catalogId: z.array(z.uuid()).optional(),
  hashtags: z.array(z.string().min(1)).optional(),
  customRegionIds: z.array(z.uuid()).optional(),
});
export type TestCmsProductsInput = z.infer<typeof testCmsProductsInput>;

// A hashtag in the expanded filter set: the ones the editor picked are
// `direct` (score 1); the rest come from `hashtag_association` rows.
export const cmsTestExpandedHashtagSchema = z.object({
  normalizedName: z.string(),
  score: z.number(),
  source: z.enum(['direct', 'association']),
});
export type CmsTestExpandedHashtag = z.infer<
  typeof cmsTestExpandedHashtagSchema
>;

export const cmsTestMatchedHashtagSchema = z.object({
  normalizedName: z.string(),
  productScore: z.number(),
  associationScore: z.number(),
});
export type CmsTestMatchedHashtag = z.infer<typeof cmsTestMatchedHashtagSchema>;

export const cmsTestResultSourceSchema = z.enum([
  'hashtag',
  'backfill',
  'random',
  'filtered',
]);
export type CmsTestResultSource = z.infer<typeof cmsTestResultSourceSchema>;

export const cmsTestScoredProductSchema = z.object({
  product: cmsProductListItemSchema,
  scoring: z.object({
    associationScore: z.number(),
    totalScore: z.number(),
    source: cmsTestResultSourceSchema,
  }),
  matchedHashtags: z.array(cmsTestMatchedHashtagSchema),
});
export type CmsTestScoredProduct = z.infer<typeof cmsTestScoredProductSchema>;

export const cmsTestProductsResultSchema = z.object({
  expandedHashtags: z.array(cmsTestExpandedHashtagSchema),
  results: z.array(cmsTestScoredProductSchema),
  meta: z.object({
    totalHashtagMatches: z.number().int().nonnegative(),
    hashtagResultCount: z.number().int().nonnegative(),
    backfillResultCount: z.number().int().nonnegative(),
    randomResultCount: z.number().int().nonnegative(),
    countAll: z.number().int().nonnegative(),
  }),
});
export type CmsTestProductsResult = z.infer<typeof cmsTestProductsResultSchema>;
